import {memo, useState, useCallback, useMemo} from "react";
import {useOutletContext} from "react-router-dom";
import {Box, Flex, HStack, Stack, Tabs, Text} from "@chakra-ui/react";
import {
  AccordionRoot,
  AccordionItem,
  AccordionItemTrigger,
  AccordionItemContent,
} from "../../../../../components/ui/accordion";
import {Button} from "../../../../../components/ui/button";
import {PencilSquareIcon, CheckIcon, PlusIcon} from "@heroicons/react/16/solid";
import {AutoResizingTextarea} from "../../../../../components/AutoResizingTextarea";
import {nominate} from "platformed-client/types";
import {
  AUDIT_PROCEDURES,
  ELC_RISKS_AND_CONTROLS,
  ELC_FINDINGS,
  ELC_EVIDENCE,
  ELC_QUESTIONNAIRES,
  HARDCODED_DIAGRAMS,
  DIAGRAM_SWIMLANES,
  DIAGRAM_NODE_CITATIONS,
  DIAGRAM_AI_GENERATIONS,
} from "../mockData";
import type {MockEvidence, MockQuestionnaire} from "../types";
import type {ElcHeaderProps, EditMeta, ProcessesOutletContext} from "../types";
import {ElcHeader} from "./ElcHeader";
import {DiagramEditor} from "./DiagramEditor";
import {useFindingsState, useRisksState} from "../hooks";
import {IdentifiedRisks, IdentifiedControls, RiskControlMatrix} from "./ProcessRisksControls";
import {FindingsCardList} from "./FindingsCards";
import {MockEvidenceSection, EvidenceDrawer} from "./ProcessEvidenceActivity";
import {QuestionnaireDrawer, ExternalQuestionnairesTable, InternalQuestionnairesTable} from "./QuestionnaireTables";
import {AiProvenance, EntitySummary} from "./EntitySummary";
import {ProcedureTable} from "./AuditProgramme";
import {DataGrid} from "./DataGrid";
import {actionTriggerStyles} from "./sharedStyles";
import {CompactMarkdown} from "./CompactMarkdown";
import {DiagramPlaceholder} from "./DiagramPlaceholder";

const ELC_DIAGRAM_LABELS: Record<string, string> = {
  "elc-it-environment": "System Architecture Diagram",
  "elc-control-environment": "Governance Structure",
};

function useElcRisksState(elcId: string) {
  const {onItemStatusChange} = useOutletContext<ProcessesOutletContext>();
  const initialRisks = useMemo(() => ELC_RISKS_AND_CONTROLS.find(e => e.elcId === elcId)?.risks ?? [], [elcId]);
  return useRisksState(initialRisks, onItemStatusChange);
}

type SectionEditState = {description: string; editMeta: EditMeta; isEditing: boolean; draft: string};

function useSectionEditState(initialDescription: string) {
  const [state, setState] = useState<SectionEditState>({
    description: initialDescription,
    editMeta: {editedBy: "ai", editedAt: new Date()},
    isEditing: false,
    draft: "",
  });

  const handleStartEdit = useCallback(() => {
    setState(prev => ({...prev, isEditing: true, draft: prev.description}));
  }, []);

  const handleCancelEdit = useCallback(() => {
    setState(prev => ({...prev, isEditing: false}));
  }, []);

  const handleSaveEdit = useCallback(() => {
    setState(prev => ({
      ...prev,
      description: prev.draft,
      editMeta: {editedBy: "human", editedAt: new Date()},
      isEditing: false,
    }));
  }, []);

  const setDraft = useCallback((draft: string) => {
    setState(prev => ({...prev, draft}));
  }, []);

  return {...state, handleStartEdit, handleCancelEdit, handleSaveEdit, setDraft};
}

const SectionDiagramInline = memo(
  ({sectionDiagramId, sectionTitle}: {sectionDiagramId: string; sectionTitle: string}) => {
    const diagramId = nominate("DiagramId", sectionDiagramId);
    const hardcodedDiagram = HARDCODED_DIAGRAMS.find(d => d.diagram_id === diagramId) ?? null;
    const swimlanes = DIAGRAM_SWIMLANES[sectionDiagramId];
    const nodeCitations = DIAGRAM_NODE_CITATIONS[sectionDiagramId];
    const aiGeneration = DIAGRAM_AI_GENERATIONS[sectionDiagramId];
    const evidence = aiGeneration?.source_evidence;

    const [diagramTabs, setDiagramTabs] = useState<{id: string; name: string}[]>(() =>
      hardcodedDiagram ? [{id: "default", name: sectionTitle}] : [],
    );
    const [activeTab, setActiveTab] = useState("default");

    const addDiagram = useCallback(() => {
      const id = crypto.randomUUID();
      setDiagramTabs(prev => [...prev, {id, name: `Diagram ${prev.length + 1}`}]);
      setActiveTab(id);
    }, []);

    return (
      <Box mt={4}>
        <HStack px={4} py={1} mb={2} justify="end">
          <HStack as="button" gap={1} {...actionTriggerStyles} onClick={addDiagram}>
            <Box as={PlusIcon} boxSize="16px" />
            Add diagram
          </HStack>
        </HStack>
        {diagramTabs.length > 0 ? (
          <Box border="1px solid {colors.gray.200}" borderRadius="md" overflow="hidden" bg="gray.100" width="100%">
            <Tabs.Root
              value={activeTab}
              onValueChange={d => setActiveTab(d.value)}
              size="sm"
              variant="enclosed"
              colorPalette="blue"
              width="100%"
            >
              <Tabs.List width="100%">
                {diagramTabs.map(tab => (
                  <Tabs.Trigger key={tab.id} value={tab.id}>
                    {tab.name}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>
              {diagramTabs.map(tab => (
                <Tabs.Content key={tab.id} value={tab.id} p={0}>
                  {tab.id === "default" && hardcodedDiagram ? (
                    <Flex direction="column" h="500px">
                      <DiagramEditor
                        diagramId={diagramId}
                        showSwimlanes={!!swimlanes && swimlanes.length > 0}
                        swimlanes={swimlanes}
                        hardcodedData={hardcodedDiagram}
                        nodeCitations={nodeCitations}
                        evidence={evidence}
                      />
                    </Flex>
                  ) : (
                    <DiagramPlaceholder />
                  )}
                </Tabs.Content>
              ))}
            </Tabs.Root>
          </Box>
        ) : (
          <DiagramPlaceholder />
        )}
      </Box>
    );
  },
);

const ElcSectionAccordionItem = memo(
  ({
    section,
    index,
    elcId,
    elcEvidence,
    openDrawer,
  }: {
    section: {title: string; description: string; diagramId?: string};
    index: number;
    elcId: string;
    elcEvidence: MockEvidence[];
    openDrawer: (ev: MockEvidence) => void;
  }) => {
    const {description, editMeta, isEditing, draft, handleStartEdit, handleCancelEdit, handleSaveEdit, setDraft} =
      useSectionEditState(section.description);

    return (
      <AccordionItem value={`section-${index}`}>
        <AccordionItemTrigger px={6} py={4} indicatorPlacement="start">
          <Text fontSize="sm" fontWeight="semibold" color="gray.800">
            {section.title}
          </Text>
        </AccordionItemTrigger>
        <AccordionItemContent p={6} bg="gray.50" borderTop="1px solid {colors.gray.200}">
          <Box bg="white" borderRadius="md" border="1px solid {colors.gray.200}" p={4} position="relative">
            {isEditing ? (
              <Stack gap={3}>
                <AutoResizingTextarea
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  fontSize="sm"
                  minRows={4}
                  placeholder="Write markdown here..."
                />
                <HStack gap={2}>
                  <Button size="xs" colorPalette="blue" onClick={handleSaveEdit}>
                    <CheckIcon />
                    Save
                  </Button>
                  <Button size="xs" variant="ghost" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                </HStack>
              </Stack>
            ) : (
              <>
                <Box
                  as="button"
                  position="absolute"
                  top={3}
                  right={3}
                  p={1}
                  borderRadius="sm"
                  color="gray.400"
                  _hover={{color: "blue.500", bg: "gray.50"}}
                  cursor="pointer"
                  onClick={handleStartEdit}
                  title={`Edit ${section.title}`}
                >
                  <Box as={PencilSquareIcon} boxSize="14px" />
                </Box>
                <Box pr={6}>
                  <CompactMarkdown>{description}</CompactMarkdown>
                </Box>
              </>
            )}
          </Box>
          <AiProvenance
            contentKey={`elc-${elcId}-section-${section.title}`}
            meta={editMeta}
            citations={elcEvidence}
            onCitationClick={openDrawer}
          />
          {section.diagramId && (
            <SectionDiagramInline sectionDiagramId={section.diagramId} sectionTitle={section.title} />
          )}
        </AccordionItemContent>
      </AccordionItem>
    );
  },
);

export const ElcOverview = memo(({headerProps}: {headerProps: ElcHeaderProps}) => {
  const elc = headerProps.elc;

  // Evidence citations & drawer
  const elcEvidence = ELC_EVIDENCE[elc.id] ?? [];
  const [drawerEvidence, setDrawerEvidence] = useState<MockEvidence | null>(null);
  const openDrawer = useCallback((ev: MockEvidence) => setDrawerEvidence(ev), []);
  const closeDrawer = useCallback(() => setDrawerEvidence(null), []);

  if (elc.sections) {
    const defaultValue = elc.sections.map((_, i) => `section-${i}`);
    return (
      <Flex direction="column" minH={0} flex={1}>
        <ElcHeader {...headerProps} />
        <AccordionRoot multiple defaultValue={defaultValue} flex={1}>
          {elc.sections.map((section, i) => (
            <ElcSectionAccordionItem
              key={i}
              section={section}
              index={i}
              elcId={elc.id}
              elcEvidence={elcEvidence}
              openDrawer={openDrawer}
            />
          ))}
        </AccordionRoot>
        <EvidenceDrawer evidence={drawerEvidence} open={drawerEvidence !== null} onClose={closeDrawer} />
      </Flex>
    );
  }

  // Non-sectioned ELC: existing two-accordion layout (Overview + Diagram)
  return (
    <ElcOverviewClassic
      headerProps={headerProps}
      elcEvidence={elcEvidence}
      openDrawer={openDrawer}
      closeDrawer={closeDrawer}
      drawerEvidence={drawerEvidence}
    />
  );
});

const ElcOverviewClassic = memo(
  ({
    headerProps,
    elcEvidence,
    openDrawer,
    closeDrawer,
    drawerEvidence,
  }: {
    headerProps: ElcHeaderProps;
    elcEvidence: MockEvidence[];
    openDrawer: (ev: MockEvidence) => void;
    closeDrawer: () => void;
    drawerEvidence: MockEvidence | null;
  }) => {
    const elc = headerProps.elc;

    // Editable description state
    const [description, setDescription] = useState(elc.description);
    const [editMeta, setEditMeta] = useState<EditMeta>({editedBy: "ai", editedAt: new Date()});
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState("");

    const handleStartEdit = useCallback(() => {
      setDraft(description);
      setIsEditing(true);
    }, [description]);

    const handleCancelEdit = useCallback(() => {
      setIsEditing(false);
    }, []);

    const handleSaveEdit = useCallback(() => {
      setDescription(draft);
      setEditMeta({editedBy: "human", editedAt: new Date()});
      setIsEditing(false);
    }, [draft]);

    // Diagram data
    const diagramId = elc.diagramId ? nominate("DiagramId", elc.diagramId) : null;
    const hardcodedDiagram = diagramId ? HARDCODED_DIAGRAMS.find(d => d.diagram_id === diagramId) : null;
    const swimlanes = elc.diagramId ? DIAGRAM_SWIMLANES[elc.diagramId] : undefined;
    const nodeCitations = elc.diagramId ? DIAGRAM_NODE_CITATIONS[elc.diagramId] : undefined;
    const aiGeneration = elc.diagramId ? DIAGRAM_AI_GENERATIONS[elc.diagramId] : undefined;
    const evidence = aiGeneration?.source_evidence;

    // Diagram tabs
    const [diagramTabs, setDiagramTabs] = useState<{id: string; name: string}[]>(() =>
      diagramId && hardcodedDiagram ? [{id: "default", name: elc.name}] : [],
    );
    const [activeTab, setActiveTab] = useState("default");

    const addDiagram = useCallback(() => {
      const id = crypto.randomUUID();
      setDiagramTabs(prev => {
        const name = `Diagram ${prev.length + 1}`;
        return [...prev, {id, name}];
      });
      setActiveTab(id);
    }, []);

    const defaultAccordion = ["overview", "diagram"];

    return (
      <Flex direction="column" minH={0} flex={1}>
        <ElcHeader {...headerProps} />

        <AccordionRoot multiple defaultValue={defaultAccordion} flex={1}>
          <AccordionItem value="overview">
            <AccordionItemTrigger px={6} py={4} indicatorPlacement="start">
              <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                Overview
              </Text>
            </AccordionItemTrigger>
            <AccordionItemContent p={6} bg="gray.50" borderTop="1px solid {colors.gray.200}">
              <Box bg="white" borderRadius="md" border="1px solid {colors.gray.200}" p={4} position="relative">
                {isEditing ? (
                  <Stack gap={3}>
                    <AutoResizingTextarea
                      value={draft}
                      onChange={e => setDraft(e.target.value)}
                      fontSize="sm"
                      minRows={4}
                      placeholder="Write markdown here..."
                    />
                    <HStack gap={2}>
                      <Button size="xs" colorPalette="blue" onClick={handleSaveEdit}>
                        <CheckIcon />
                        Save
                      </Button>
                      <Button size="xs" variant="ghost" onClick={handleCancelEdit}>
                        Cancel
                      </Button>
                    </HStack>
                  </Stack>
                ) : (
                  <>
                    <Box
                      as="button"
                      position="absolute"
                      top={3}
                      right={3}
                      p={1}
                      borderRadius="sm"
                      color="gray.400"
                      _hover={{color: "blue.500", bg: "gray.50"}}
                      cursor="pointer"
                      onClick={handleStartEdit}
                      title="Edit overview"
                    >
                      <Box as={PencilSquareIcon} boxSize="14px" />
                    </Box>
                    <Box pr={6}>
                      <CompactMarkdown>{description}</CompactMarkdown>
                    </Box>
                  </>
                )}
              </Box>
              <AiProvenance
                contentKey={`elc-${elc.id}-understanding`}
                meta={editMeta}
                citations={elcEvidence}
                onCitationClick={openDrawer}
              />
            </AccordionItemContent>
          </AccordionItem>

          {/* Diagram */}
          <AccordionItem value="diagram">
            <AccordionItemTrigger px={6} py={4} indicatorPlacement="start">
              <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                {ELC_DIAGRAM_LABELS[elc.id] ?? "Diagram"}
              </Text>
            </AccordionItemTrigger>
            <AccordionItemContent p={6} bg="gray.50" borderTop="1px solid {colors.gray.200}">
              <HStack px={4} py={1} mt={-2} mb={2} justify="end">
                <HStack as="button" gap={1} {...actionTriggerStyles} onClick={addDiagram}>
                  <Box as={PlusIcon} boxSize="16px" />
                  Add diagram
                </HStack>
              </HStack>
              {diagramTabs.length > 0 ? (
                <Box
                  border="1px solid {colors.gray.200}"
                  borderRadius="md"
                  overflow="hidden"
                  bg="gray.100"
                  width="100%"
                >
                  <Tabs.Root
                    value={activeTab}
                    onValueChange={d => setActiveTab(d.value)}
                    size="sm"
                    variant="enclosed"
                    colorPalette="blue"
                    width="100%"
                  >
                    <Tabs.List width="100%">
                      {diagramTabs.map(tab => (
                        <Tabs.Trigger key={tab.id} value={tab.id}>
                          {tab.name}
                        </Tabs.Trigger>
                      ))}
                    </Tabs.List>
                    {diagramTabs.map(tab => (
                      <Tabs.Content key={tab.id} value={tab.id} p={0}>
                        {tab.id === "default" && diagramId && hardcodedDiagram ? (
                          <Flex direction="column" h="500px">
                            <DiagramEditor
                              diagramId={diagramId}
                              showSwimlanes={!!swimlanes && swimlanes.length > 0}
                              swimlanes={swimlanes}
                              hardcodedData={hardcodedDiagram}
                              nodeCitations={nodeCitations}
                              evidence={evidence}
                            />
                          </Flex>
                        ) : (
                          <DiagramPlaceholder />
                        )}
                      </Tabs.Content>
                    ))}
                  </Tabs.Root>
                </Box>
              ) : (
                <DiagramPlaceholder />
              )}
              {diagramId && hardcodedDiagram && (
                <AiProvenance
                  contentKey={`elc-${elc.id}-diagram`}
                  meta={editMeta}
                  citations={elcEvidence}
                  onCitationClick={openDrawer}
                />
              )}
            </AccordionItemContent>
          </AccordionItem>
        </AccordionRoot>

        <EvidenceDrawer evidence={drawerEvidence} open={drawerEvidence !== null} onClose={closeDrawer} />
      </Flex>
    );
  },
);

export {ElcComplexity} from "./ElcComplexityPage";

export const ElcRisks = memo(({headerProps}: {headerProps: ElcHeaderProps}) => {
  const {risks, addRisk, deleteRisk, updateRiskStatus} = useElcRisksState(headerProps.elc.id);
  return (
    <Flex direction="column" minH={0} flex={1}>
      <ElcHeader {...headerProps} />
      <IdentifiedRisks risks={risks} addRisk={addRisk} deleteRisk={deleteRisk} updateRiskStatus={updateRiskStatus} />
    </Flex>
  );
});

export const ElcControls = memo(({headerProps}: {headerProps: ElcHeaderProps}) => {
  const {allControls, addControl, deleteControl, updateControlStatus} = useElcRisksState(headerProps.elc.id);
  return (
    <Flex direction="column" minH={0} flex={1}>
      <ElcHeader {...headerProps} />
      <IdentifiedControls
        allControls={allControls}
        addControl={addControl}
        deleteControl={deleteControl}
        updateControlStatus={updateControlStatus}
      />
    </Flex>
  );
});

export const ElcRiskControlMatrix = memo(({headerProps}: {headerProps: ElcHeaderProps}) => {
  const {risks, allControls, toggleMapping} = useElcRisksState(headerProps.elc.id);
  return (
    <Flex direction="column" minH={0} flex={1}>
      <ElcHeader {...headerProps} />
      <RiskControlMatrix risks={risks} allControls={allControls} toggleMapping={toggleMapping} />
    </Flex>
  );
});

function useElcFindingsState(elcId: string) {
  const initialFindings = useMemo(() => ELC_FINDINGS.find(e => e.elcId === elcId)?.findings ?? [], [elcId]);
  return useFindingsState(initialFindings);
}

export const ElcFindings = memo(({headerProps}: {headerProps: ElcHeaderProps}) => {
  const findingsState = useElcFindingsState(headerProps.elc.id);
  const {allControls} = useElcRisksState(headerProps.elc.id);
  return (
    <Flex direction="column" minH={0} flex={1}>
      <ElcHeader {...headerProps} />
      <Box p={6} bg="gray.50" flex={1}>
        <FindingsCardList {...findingsState} allControls={allControls} />
      </Box>
    </Flex>
  );
});

// ---------------------------------------------------------------------------
// Questionnaires
// ---------------------------------------------------------------------------

export const ElcQuestionnaires = memo(({headerProps}: {headerProps: ElcHeaderProps}) => {
  const elcId = headerProps.elc.id;
  const initial = ELC_QUESTIONNAIRES[elcId] ?? [];
  const [items, setItems] = useState<MockQuestionnaire[]>(initial);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<MockQuestionnaire | null>(null);
  const [drawerEvidence, setDrawerEvidence] = useState<MockEvidence | null>(null);

  const externals = useMemo(() => items.filter(i => i.type === "external"), [items]);
  const internals = useMemo(() => items.filter(i => i.type === "internal"), [items]);

  const openDrawer = useCallback((item: MockQuestionnaire) => setSelectedQuestionnaire(item), []);
  const closeDrawer = useCallback(() => setSelectedQuestionnaire(null), []);

  const openEvidenceDrawer = useCallback((ev: MockEvidence) => setDrawerEvidence(ev), []);
  const closeEvidenceDrawer = useCallback(() => setDrawerEvidence(null), []);

  const nextCitationId = useCallback(
    (prev: MockQuestionnaire[]) => {
      const evidenceMax = (ELC_EVIDENCE[elcId] ?? []).reduce((max, e) => Math.max(max, e.citationId), 0);
      const questionnaireMax = prev.reduce((max, q) => Math.max(max, q.questionnaire.citationId), 0);
      return Math.max(evidenceMax, questionnaireMax) + 1;
    },
    [elcId],
  );

  const addExternal = useCallback(() => {
    const id = crypto.randomUUID();
    setItems(prev => [
      ...prev,
      {
        type: "external",
        questionnaire: {
          id,
          citationId: nextCitationId(prev),
          templateName: "New External Questionnaire",
          status: "draft",
          createdAt: new Date(),
          submittedAt: null,
          lockedAt: null,
          responseCount: 0,
          totalQuestions: 0,
          questions: [],
        },
      },
    ]);
  }, [nextCitationId]);

  const addInternal = useCallback(() => {
    const id = crypto.randomUUID();
    setItems(prev => [
      ...prev,
      {
        type: "internal",
        questionnaire: {
          id,
          citationId: nextCitationId(prev),
          title: "New Internal Questionnaire",
          status: "not-started",
          assignee: "Unassigned",
          completedCount: 0,
          totalQuestions: 0,
          createdAt: new Date(),
          completedAt: null,
          questions: [],
        },
      },
    ]);
  }, [nextCitationId]);

  return (
    <Flex direction="column" minH={0} flex={1}>
      <ElcHeader {...headerProps} />
      <AccordionRoot multiple defaultValue={["external", "internal", "evidence"]} flex={1}>
        <AccordionItem value="external">
          <AccordionItemTrigger px={6} py={4} indicatorPlacement="start">
            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
              External Questionnaires
            </Text>
          </AccordionItemTrigger>
          <AccordionItemContent p={0} bg="gray.50" borderTop="1px solid {colors.gray.200}">
            <Box p={6}>
              <ExternalQuestionnairesTable items={externals} onOpen={openDrawer} onAdd={addExternal} />
            </Box>
          </AccordionItemContent>
        </AccordionItem>

        <AccordionItem value="internal">
          <AccordionItemTrigger px={6} py={4} indicatorPlacement="start">
            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
              Internal Questionnaires
            </Text>
          </AccordionItemTrigger>
          <AccordionItemContent p={0} bg="gray.50" borderTop="1px solid {colors.gray.200}">
            <Box p={6}>
              <InternalQuestionnairesTable items={internals} onOpen={openDrawer} onAdd={addInternal} />
            </Box>
          </AccordionItemContent>
        </AccordionItem>

        <AccordionItem value="evidence">
          <AccordionItemTrigger px={6} py={4} indicatorPlacement="start">
            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
              Relevant Evidence
            </Text>
          </AccordionItemTrigger>
          <AccordionItemContent bg="gray.50" borderTop="1px solid {colors.gray.200}">
            <MockEvidenceSection initialEvidence={ELC_EVIDENCE[elcId] ?? []} onViewEvidence={openEvidenceDrawer} />
          </AccordionItemContent>
        </AccordionItem>
      </AccordionRoot>
      <QuestionnaireDrawer item={selectedQuestionnaire} open={selectedQuestionnaire !== null} onClose={closeDrawer} />
      <EvidenceDrawer evidence={drawerEvidence} open={drawerEvidence !== null} onClose={closeEvidenceDrawer} />
    </Flex>
  );
});

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------

export const ELC_SUMMARIES: Record<string, string> = {
  "elc-control-environment": `The control environment is assessed as **satisfactory**. The organisation demonstrates a strong commitment to integrity and ethical values, with a mature governance structure and well-documented HR policies. Two Low findings were identified — both relate to documentation and process hygiene rather than fundamental control design gaps.

## Audit focus areas

**Governance and board oversight** is functioning well. The three-lines governance model is clearly established, the Audit Committee is appropriately composed with relevant expertise, and the quarterly reporting package (CE-03, 3/3) provides comprehensive information to the board. The Low finding on meeting minutes documentation is a process improvement opportunity but does not undermine the quality of governance oversight itself. We should review a sample of reporting packages to confirm the quality and completeness of information provided, but can take a reduced testing approach.

**Tone at the top** is evidenced through the Code of Conduct attestation process, the quarterly Financial Reporting Integrity Forum, and the ethics training programme. The ethics training shortfall (94% vs 100% target) is explained by employees on extended leave, but the absence of a catch-up mechanism (CE-02, 3/2) is a gap that should be addressed. The zero-retaliation policy and independent whistleblower hotline provide appropriate channels for raising concerns.

**Competence and accountability** is well-managed. The annual competence assessment (CE-05, 3/3) provides documented evaluation, CPD requirements are enforced, and succession planning is in place for critical roles. The most recent assessment identified no significant gaps, which supports our reliance on management's financial reporting capabilities.

## Reliance and testing strategy

We can place reliance on CE-01 (Code of Conduct Attestation), CE-03 (Quarterly Board Reporting), and CE-05 (Annual Competence Assessment) — all are rated fully effective or near-fully effective. For CE-04 (Critical Issue Escalation Protocol), partial reliance is appropriate given the partially effective implementation rating. The control environment assessment supports a controls-reliance approach across the broader audit.

## Recommendations for the entity

We should recommend that the entity update the Audit Committee meeting minutes template to include a structured action item register with owners and due dates, and implement an automatic re-enrolment mechanism in the training management system for employees returning from extended leave.`,

  "elc-it-environment": `The GITC environment is assessed as **requiring attention**. The deprovisioning finding (High) and the significant residual risk on access provisioning are the dominant themes — both point to a gap in the access lifecycle that should drive our audit response.

## Audit focus areas

**User access lifecycle** should be the primary area of focus. The absence of an automated Workday → Azure AD integration means joiner/mover/leaver processing depends on manual email notification, which has demonstrably failed for terminations. We should design substantive procedures around the completeness and timeliness of deprovisioning, including a full population analysis of termination-to-revocation lag rather than relying on the entity's sample of 20. The quarterly user access review (GITC-02) is only partially effective in implementation — we should assess whether we can place any reliance on it or whether we need to perform our own independent recertification testing.

**Change management** is in better shape. The CAB process (GITC-03) is fully effective and the segregation of development and production (GITC-04) is sound. The Low finding on CAB meeting documentation is a documentation hygiene issue rather than a control design gap — we can likely take a reduced testing approach here, focused on confirming that test evidence and rollback plans exist for a sample of higher-risk changes.

**Privileged access** warrants targeted procedures. With 14 privileged accounts across production and GITC-05 rated only partially effective (2/2), we should evaluate whether the monthly CyberArk review cadence is sufficient and consider requesting session recording logs for a sample of privileged sessions during key processing periods.

## Reliance and testing strategy

We can likely place reliance on change management controls (GITC-03, GITC-04) and reduce the extent of substantive testing in that domain. For access management, the control weaknesses mean we should plan for a primarily substantive approach — direct testing of user access appropriateness rather than reliance on the entity's review controls. The privileged access domain sits in between: partial reliance is possible if we can validate the CyberArk monitoring is catching anomalies, but we should supplement with targeted substantive procedures.

## Recommendations for the entity

We should recommend that the entity prioritise the Workday → Azure AD automated integration ahead of other IT initiatives, implement weekly reconciliation of active accounts against the HR master record as an interim measure, and update the CAB meeting template to include mandatory risk assessment fields.`,

  "elc-risk-assessment": `The entity's risk assessment process is assessed as **requiring improvement**. While a formal risk register exists and is maintained with quarterly governance, the process has notable gaps in change responsiveness and methodology currency that affect our confidence in the completeness of risk identification.

## Audit focus areas

**Risk identification completeness** is generally adequate for established risk categories. The quarterly risk register review (RA-01, 3/3) and business unit risk champion network (RA-02, 3/2) provide a reasonable foundation. However, the partial implementation of the champion network means that risk identification remains stronger for regulatory and compliance risks than for operational and market risks. We should independently assess whether all business risks with financial reporting implications have been captured, particularly around the recent acquisition and customer loss.

**Change responsiveness** is the highest-risk area. RA-R02 carries significant residual risk, driven by the finding that 3 of 5 recent significant changes were not fully addressed through the entity's change impact assessment process (RA-03, 3/2). The WMS migration and acquisition integration are particularly concerning — both have direct implications for financial reporting controls that remain unaddressed. We should design procedures to assess the financial reporting impact of these changes independently.

**Risk-to-control linkage** is an area of weakness. The risk-to-control mapping exercise (RA-04, 2/2) is only partially effective in both design and implementation, meaning there is limited formal evidence that all identified risks have corresponding control responses. This affects our ability to leverage the entity's own risk assessment in planning our audit procedures.

## Reliance and testing strategy

We can place reliance on RA-01 (Quarterly Risk Register Review) for the completeness of established risk categories. For change-related risks, we should take a primarily substantive approach — our own assessment of financial reporting impacts rather than reliance on the entity's change impact process. The risk-to-control mapping should be independently verified rather than relied upon.

## Recommendations for the entity

We should recommend that the entity formalise trigger criteria for mandatory change impact assessments (not just regulatory changes), establish a formal annual review cycle for the risk assessment methodology, document a board-approved risk appetite statement, and enhance the risk register with version history and formal linkage to control activities.`,

  "elc-monitoring-governance": `The monitoring of controls framework is assessed as **requiring improvement**. The significant residual risk on outsourced service provider monitoring, the SLA remediation finding (Moderate), and the stale exception monitoring rules collectively indicate that the entity's self-monitoring mechanisms have gaps that affect our audit reliance.

## Audit focus areas

**Outsourced service provider monitoring** is the highest-risk area. MOC-R03 is significant with residual uncertainty at 4, and the annual SOC report review (MOC-05) is only partially effective in implementation. The interim monitoring control (MOC-06) is also partially effective, meaning there is limited assurance between annual SOC report periods. We should independently review the most recent SOC 1 Type II reports, assess complementary user entity controls, and evaluate whether bridging letters or interim attestations are available. For higher-risk providers, we may need to perform additional procedures.

**Ongoing monitoring effectiveness** is a concern. The automated exception monitoring rules have not been reviewed or updated in over 12 months (Low finding), during which new transaction types and business process changes were implemented. The monthly KPI dashboard (MOC-01, 3/3) provides good high-level oversight, but the gap in exception rule maintenance (MOC-02, 3/2) means automated detective controls may not be catching relevant anomalies in newer processes.

**Deficiency remediation effectiveness** directly affects our confidence in the entity's ability to address findings across all processes. The SLA finding (Moderate) — 4 of 12 deficiencies exceeding the 60-day SLA, 2 open for 120+ days — suggests the spreadsheet-based tracker is not driving timely remediation. We should cross-reference the entity's deficiency register against our own findings to assess whether issues will be addressed in a timely manner.

## Reliance and testing strategy

We can place reliance on MOC-01 (Monthly KPI Dashboard Review) and MOC-04 (Remediation Re-Testing) — both are fully effective. For MOC-02 (Automated Exception Alerting) and MOC-03 (Deficiency Tracking), we should take a primarily substantive approach given the partial effectiveness ratings. For outsourced provider monitoring (MOC-05, MOC-06), substantive procedures are necessary to close the assurance gap.

## Recommendations for the entity

We should recommend that the entity establish a formal quarterly review cycle for automated exception monitoring rules, implement automated SLA breach alerting ahead of the GRC platform migration in Q3 2026, and supplement the annual SOC report review with formal interim monitoring procedures for critical service providers.`,
};

const ELC_FALLBACK_SUMMARY = `The entity-level control assessment has been completed. A detailed narrative will be generated once the full risk and control evaluation is finalised.`;

function getElcSummary(elcId: string): string {
  return ELC_SUMMARIES[elcId] ?? ELC_FALLBACK_SUMMARY;
}

const ElcProcedures = memo(({elcId}: {elcId: string}) => {
  const procedures = useMemo(() => AUDIT_PROCEDURES.filter(p => p.elcId === elcId), [elcId]);
  if (procedures.length === 0) return null;
  return (
    <Box>
      <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" mb={3}>
        Planned Audit Procedures
      </Text>
      <DataGrid.Container>
        <ProcedureTable procedures={procedures} />
      </DataGrid.Container>
    </Box>
  );
});

export const ElcSummary = memo(({headerProps}: {headerProps: ElcHeaderProps}) => {
  const elcId = headerProps.elc.id;
  const {isSimplified} = useOutletContext<ProcessesOutletContext>();

  return (
    <EntitySummary
      contentKey={`elc-${elcId}-summary`}
      header={<ElcHeader {...headerProps} />}
      initialContent={getElcSummary(elcId)}
      footer={isSimplified ? undefined : <ElcProcedures elcId={elcId} />}
    />
  );
});
